/**
 * AgentConversationManager — manages agent thread IDs, routing decisions,
 * and the streaming integration for the visual editor agent.
 * Extracted from AIPropertiesChat to centralize agent conversation logic.
 */
import { chatApi, type ChatResponse } from '@/services/api/agentApi';
import {
  formatPhase11BBindingInstructionsForPrompt,
  formatPhase11CWriteTargetInstructionsForPrompt,
  summarizeBindingContextForAi,
  type AiBindingContextSummary,
  type AiBindingSourceIdentitySummary,
  type AiBindingWriteTargetSummary,
} from '@/services/ai/BindingContextSummary';
import type { ComponentDataWriteOperation } from '@/types/componentDataBinding';
import { visualEditorApi, type EditorContext, type AgentVisualResponse } from '@/services/api/agentApi';
import { agentStreamingService, type StreamingCallbacks } from '@/services/editor/AgentStreamingService';
import {
  startStreamingBatch,
  applyIncremental,
  commitStreamingBatch,
  rollbackStreamingBatch,
  getAndClearCloningDetails,
} from '@/services/editor/CanvasActionApplicator';
import * as sessionManager from '@/services/editor/CanvasSessionManager';
import { useDebugStore } from '@/stores/ai/debug-store';
import { sanitizeVisualArtifactsForDownstreamPrompt } from '@/services/editor/planArtifactCanonicalization';
import type { PlanSection } from '@/types/plan';

// localStorage key helpers

const WRITE_OPERATIONS: ComponentDataWriteOperation[] = ['create', 'update', 'delete'];

interface SelectedAgentComponent {
  type: string;
  name?: string;
  id: string;
  properties?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getLegacySchemaIds(properties: Record<string, unknown> | undefined): string[] | null {
  const dataSource = properties?.dataSource;
  if (!isRecord(dataSource)) {
    return null;
  }

  const schemaIds = new Set<string>();
  const queryDefinition = dataSource.queryDefinition;
  if (isRecord(queryDefinition) && typeof queryDefinition.schemaId === 'string') {
    schemaIds.add(queryDefinition.schemaId);
  }

  const compositeQueryDefinition = dataSource.compositeQueryDefinition;
  const sources = isRecord(compositeQueryDefinition)
    ? compositeQueryDefinition.sources
    : undefined;
  if (Array.isArray(sources)) {
    for (const source of sources) {
      if (isRecord(source) && typeof source.schemaId === 'string') {
        schemaIds.add(source.schemaId);
      }
    }
  }

  return Array.from(schemaIds).slice(0, 5);
}

function formatSourceIdentity(source: AiBindingSourceIdentitySummary): string {
  if (source.kind === 'schema') {
    return [
      'schema',
      `dataOwnerOrganizationId=${source.dataOwnerOrganizationId}`,
      `ownerType=${source.ownerType}`,
      `ownerId=${source.ownerId}`,
      `groupId=${source.groupId}`,
      `schemaId=${source.schemaId}`,
    ].join(' ');
  }

  if (source.kind === 'static') {
    return `static adapterId=${source.adapterId}`;
  }

  if (source.kind === 'unknown') {
    return 'unknown';
  }

  return [
    source.kind,
    `ownerType=${source.ownerType}`,
    `ownerId=${source.ownerId}`,
    `viewOrganizationId=${source.viewOrganizationId}`,
    `viewId=${source.viewId}`,
  ].join(' ');
}

function formatFieldMapping(summary: AiBindingContextSummary): string {
  const mappingEntries = Object.entries(summary.fieldMapping)
    .slice(0, 8)
    .map(([componentField, sourceField]) =>
      `${componentField}->${sourceField || 'unmapped'}`,
    );

  return mappingEntries.length > 0 ? mappingEntries.join(', ') : 'none';
}

function formatReadiness(
  readiness: Partial<Record<ComponentDataWriteOperation, string>> | undefined,
): string | null {
  if (readiness === undefined) {
    return null;
  }

  const readinessEntries = WRITE_OPERATIONS.flatMap((operation) => {
    const value = readiness[operation];
    return value === undefined ? [] : [`${operation}=${value}`];
  });

  return readinessEntries.length > 0 ? readinessEntries.join(', ') : null;
}

function formatWriteTarget(writeTarget: AiBindingWriteTargetSummary): string {
  if (!writeTarget.present) {
    return 'absent';
  }

  if ('kind' in writeTarget && writeTarget.kind === 'federated-view-source') {
    const readiness = formatReadiness(writeTarget.readiness);
    const readinessSuffix = readiness === null ? '' : ` readiness ${readiness}`;
    return [
      'present federated-view-source',
      `targetSourceId=${writeTarget.targetSourceId}`,
      `view=${formatSourceIdentity(writeTarget.view)}`,
      `source=${formatSourceIdentity(writeTarget.source)}`,
    ].join(' ') + readinessSuffix;
  }

  if ('target' in writeTarget) {
    const readiness = formatReadiness(writeTarget.readiness);
    const readinessSuffix = readiness === null ? '' : ` readiness ${readiness}`;
    return `present ${formatSourceIdentity(writeTarget.target)}${readinessSuffix}`;
  }

  return 'absent';
}


function formatLabels(summary: AiBindingContextSummary): string | null {
  const labels: string[] = [];
  if (summary.sourceLabels) {
    labels.push(
      `source=${summary.sourceLabels.schemaDisplayName}`,
      `group=${summary.sourceLabels.groupName}`,
    );
  }

  if (summary.viewLabels) {
    labels.push(`view=${summary.viewLabels.name}`);
    if (summary.viewLabels.ownerName) {
      labels.push(`viewOwner=${summary.viewLabels.ownerName}`);
    }
  }

  return labels.length > 0 ? labels.join(', ') : null;
}

function formatBindingContextSummary(summary: AiBindingContextSummary): string {
  const parts = [
    `Binding context: ${summary.status}`,
    `source ${formatSourceIdentity(summary.source)}`,
  ];

  if (summary.reconnectReason) {
    parts.push(`reason ${summary.reconnectReason}`);
  }

  const labels = formatLabels(summary);
  if (labels) {
    parts.push(`labels ${labels}`);
  }

  parts.push(`field mappings ${formatFieldMapping(summary)}`);
  parts.push(`write target ${formatWriteTarget(summary.writeTarget)}`);

  return parts.join('; ');
}

function buildSelectedComponentDataContext(
  component: SelectedAgentComponent,
): string | null {
  if (component.properties?.dataBinding !== undefined) {
    return formatBindingContextSummary(summarizeBindingContextForAi(component));
  }

  const schemaIds = getLegacySchemaIds(component.properties);
  if (schemaIds !== null) {
    return schemaIds.length > 0
      ? `Legacy data source schema IDs: ${schemaIds.join(', ')}`
      : 'Legacy data source schema IDs: none';
  }

  return formatBindingContextSummary(summarizeBindingContextForAi(component));
}

const getAgentThreadKey = (projectId: string): string =>
  `terramatrix_agent_thread_${projectId}`;
const getVisualEditorThreadKey = (projectId: string): string =>
  `terramatrix_visual_editor_thread_${projectId}`;

export interface AgentThreadState {
  agentThreadId: string | null;
  visualEditorThreadId: string | null;
}

export interface StreamingProgress {
  isStreaming: boolean;
  currentStep: string | null;
  completedSteps: string[];
  statusMessage: string | null;
}

/**
 * Map agent ChatResponse to a property message shape.
 */
export function mapAgentResponseToMessage(response: ChatResponse): {
  content: string;
  metadata: Record<string, unknown>;
} {
  return {
    content: response.response,
    metadata: {
      source: 'agent',
      threadId: response.threadId,
      modelName: response.usage?.modelName,
      modelTier: response.usage?.modelTier,
      memoriesCreated: response.memoriesCreated,
      eventsCreated: response.eventsCreated,
      agentData: response.metadata,
    },
  };
}

/**
 * Build editor context string for agent enrichment.
 */
export function buildAgentContext(
  currentPageId: string | null,
  selectedComponent: SelectedAgentComponent | null,
  componentCount: number,
): string {
  const parts: string[] = [];

  if (currentPageId) {
    parts.push(`Current page: ${currentPageId}`);
  }

  if (selectedComponent) {
    parts.push(`Selected component: ${selectedComponent.type} "${selectedComponent.name || selectedComponent.id}"`);

    const dataComponentTypes = [
      'chart', 'bar-chart', 'line-chart', 'pie-chart', 'scatter-chart',
      'area-chart', 'heatmap-chart', 'treemap-chart', 'gauge-chart',
      'vertical-gauge-chart', 'map-chart', 'data-card', 'data-text',
      'fermentation-tank-card', 'table', 'advanced-table',
      'kanban-board', 'checklist', 'horizontal-timeline', 'assignment-widget',
      'gantt-chart', 'calendar', 'project-hub', 'questionnaire', 'voting-card',
      'time-tracker',
    ];
    if (dataComponentTypes.includes(selectedComponent.type)) {
      const dataContext = buildSelectedComponentDataContext(selectedComponent);
      if (dataContext) {
        parts.push(dataContext);
      }
    }
  }

  parts.push(`Canvas has ${componentCount} components`);
  return parts.join('\n');
}

/**
 * Route a message to the chat agent (non-visual, text-based).
 */
export async function routeToAgent(
  userInput: string,
  communityId: string | undefined,
  threadId: string | null,
  contextPrefix?: string,
): Promise<{ response: ChatResponse; threadId: string | null }> {
  const enrichedMessage = contextPrefix
    ? `[Editor Context: ${contextPrefix}]\n\n${userInput}`
    : userInput;

  const result = await chatApi.send(
    enrichedMessage,
    communityId,
    threadId || undefined,
  );

  if (!result.data) {
    throw new Error('Agent service returned empty response');
  }

  return {
    response: result.data,
    threadId: result.data.threadId || null,
  };
}

/**
 * Route a message to the visual editor agent (non-streaming).
 */
export async function routeToVisualEditor(
  message: string,
  editorContext: EditorContext,
  threadId?: string,
): Promise<AgentVisualResponse> {
  const result = await visualEditorApi.execute(message, editorContext, threadId);
  if (!result.data) {
    throw new Error('Visual editor agent returned empty response');
  }
  return result.data;
}

/**
 * Route a message to the visual editor agent with SSE streaming.
 * Applies actions incrementally as they arrive.
 */
export async function routeToVisualEditorStreaming(
  message: string,
  editorContext: EditorContext,
  callbacks: StreamingCallbacks & {
    onStreamingStart?: () => void;
    onStreamingEnd?: (response: AgentVisualResponse | null) => void;
  },
  threadId?: string,
  sessionContext?: string,
  classificationMetadata?: {
    isFollowUp: boolean;
    complexity: string;
    mentionedBreakpoints: string[];
    followUpAgent?: string;
  },
  extraBody?: Record<string, unknown>,
): Promise<void> {
  let finalResponse: AgentVisualResponse | null = null;
  const ds = useDebugStore.getState;

  startStreamingBatch(message, threadId);
  callbacks.onStreamingStart?.();
  if (ds().isEnabled) ds().setStreamingStart();

  try {
    await agentStreamingService.stream(
      message,
      editorContext,
      {
        onThread: (tid) => {
          callbacks.onThread?.(tid);
          if (ds().isEnabled) {
            ds().setThreadId(tid);
            ds().pushEvent({ type: 'thread', data: { threadId: tid }, timestamp: Date.now() });
          }
        },
        onStepStart: (data) => {
          callbacks.onStepStart?.(data);
          if (ds().isEnabled) ds().pushEvent({ type: 'step_start', data, timestamp: Date.now() });
        },
        onActions: (data) => {
          // Apply actions incrementally during streaming
          if (!data.requiresConfirmation) {
            const result = applyIncremental(data.step, data.actions);
            // Record in session
            if (threadId || callbacks.onThread) {
              const tid = threadId || '';
              sessionManager.recordActions(tid, message, data.step, data.actions);
            }
            if (result.failed.length > 0) {
              console.warn('AgentConversationManager: some actions failed', result.failed);
            }
            // Debug: forward applicator result + cloning details
            if (ds().isEnabled) {
              ds().pushEvent({ type: 'actions', data, timestamp: Date.now() });
              const cloningDetails = getAndClearCloningDetails();
              ds().recordApplicatorResult(data.step, { ...result, cloningDetails });
            }
          }
          callbacks.onActions?.(data);
        },
        onStepComplete: (data) => {
          callbacks.onStepComplete?.(data);
          if (ds().isEnabled) ds().pushEvent({ type: 'step_complete', data, timestamp: Date.now() });
        },
        onMessage: (data) => {
          callbacks.onMessage?.(data);
          if (ds().isEnabled) ds().pushEvent({ type: 'message', data, timestamp: Date.now() });
        },
        onComplete: (data) => {
          finalResponse = data;
          commitStreamingBatch();
          callbacks.onComplete?.(data);
          // Debug: capture reasoning chain and completion data
          if (ds().isEnabled) {
            ds().pushEvent({ type: 'complete', data, timestamp: Date.now() });
            if (finalResponse.reasoningChain) ds().setReasoningChain(finalResponse.reasoningChain);
            ds().setCompletionData(finalResponse);
          }
        },
        onError: (data) => {
          // On error, commit partial work (don't rollback)
          commitStreamingBatch();
          callbacks.onError?.(data);
          if (ds().isEnabled) ds().pushEvent({ type: 'error', data, timestamp: Date.now() });
        },
        onMultiPagePlan: (data) => {
          callbacks.onMultiPagePlan?.(data);
          if (ds().isEnabled) ds().pushEvent({ type: 'multi_page_plan', data, timestamp: Date.now() });
        },
        onPageStart: (data) => {
          callbacks.onPageStart?.(data);
          if (ds().isEnabled) ds().pushEvent({ type: 'page_start', data, timestamp: Date.now() });
        },
        onPageComplete: (data) => {
          callbacks.onPageComplete?.(data);
          if (ds().isEnabled) ds().pushEvent({ type: 'page_complete', data, timestamp: Date.now() });
        },
        onLockConflict: (data) => {
          callbacks.onLockConflict?.(data);
          if (ds().isEnabled) ds().pushEvent({ type: 'lock_conflict', data, timestamp: Date.now() });
        },
        onMemoryCreated: (data) => {
          callbacks.onMemoryCreated?.(data);
          if (ds().isEnabled) ds().pushEvent({ type: 'memory_created', data, timestamp: Date.now() });
        },
        onVerificationResult: (data) => {
          callbacks.onVerificationResult?.(data);
          if (ds().isEnabled) ds().pushEvent({ type: 'verification_result', data, timestamp: Date.now() });
        },
        onDiagnostic: (data) => {
          callbacks.onDiagnostic?.(data);
          if (ds().isEnabled) ds().pushEvent({ type: 'diagnostic', data, timestamp: Date.now() });
        },
        onSubAgentRetry: (data) => {
          callbacks.onSubAgentRetry?.(data);
          if (ds().isEnabled) ds().pushEvent({ type: 'sub_agent_retry', data, timestamp: Date.now() });
        },
        onSubAgentRetryResult: (data) => {
          callbacks.onSubAgentRetryResult?.(data);
          if (ds().isEnabled) ds().pushEvent({ type: 'sub_agent_retry_result', data, timestamp: Date.now() });
        },
        onSubAgentAttemptsSummary: (data) => {
          callbacks.onSubAgentAttemptsSummary?.(data);
          if (ds().isEnabled) ds().pushEvent({ type: 'sub_agent_attempts_summary', data, timestamp: Date.now() });
        },
        onPipelineTiming: (data) => {
          callbacks.onPipelineTiming?.(data);
          if (ds().isEnabled) ds().pushEvent({ type: 'pipeline_timing', data, timestamp: Date.now() });
        },
      },
      threadId,
      sessionContext,
      classificationMetadata,
      extraBody,
    );
  } catch (error) {
    commitStreamingBatch();
    if (ds().isEnabled) {
      ds().pushEvent({
        type: 'error',
        data: { message: error instanceof Error ? error.message : String(error) },
        timestamp: Date.now(),
      });
    }
    throw error;
  } finally {
    // Ensure batch is always committed even if stream closes without 'complete' event
    commitStreamingBatch();
    if (ds().isEnabled) {
      ds().setStreamingEnd();
      ds().endSession();
    }
    callbacks.onStreamingEnd?.(finalResponse);
  }
}

/**
 * Abort the current streaming request and rollback canvas changes.
 */
export function abortStreaming(): void {
  agentStreamingService.abort();
  rollbackStreamingBatch();
}

/**
 * Load a persisted thread ID from localStorage.
 */
export function loadPersistedThreadId(projectId: string, type: 'agent' | 'visual-editor'): string | null {
  try {
    const key = type === 'agent' ? getAgentThreadKey(projectId) : getVisualEditorThreadKey(projectId);
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Persist a thread ID to localStorage.
 */
export function persistThreadId(projectId: string, type: 'agent' | 'visual-editor', threadId: string): void {
  try {
    const key = type === 'agent' ? getAgentThreadKey(projectId) : getVisualEditorThreadKey(projectId);
    localStorage.setItem(key, threadId);
  } catch {
    console.warn('AgentConversationManager: Failed to persist thread ID');
  }
}

/**
 * Clear a persisted thread ID.
 */
export function clearPersistedThreadId(projectId: string, type: 'agent' | 'visual-editor'): void {
  try {
    const key = type === 'agent' ? getAgentThreadKey(projectId) : getVisualEditorThreadKey(projectId);
    localStorage.removeItem(key);
  } catch {
    // Ignore
  }
}

/** Accessibility audit patterns for chat message detection */
const A11Y_PATTERNS = [
  /\b(check|run|audit|test)\b.*\b(accessibility|a11y|wcag)\b/i,
  /\b(accessibility|a11y|wcag)\b.*\b(check|audit|test|scan)\b/i,
  /\bwcag\s+(compliance|audit|check)\b/i,
  /\baccessibility\s+(report|score|issues?)\b/i,
];

/** Check if a user message is requesting an accessibility audit. */
export function isAccessibilityRequest(message: string): boolean {
  return A11Y_PATTERNS.some(p => p.test(message));
}

/** Route to the accessibility audit service and return the result. */
export async function routeToAccessibilityAudit(
  editorContext: EditorContext,
): Promise<import('@/services/api/agentApi').AccessibilityAuditResult | null> {
  try {
    const result = await visualEditorApi.auditAccessibility(editorContext);
    return result?.data || null;
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Plan Mode Utilities
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Topological sort of plan sections/phases by dependsOn.
 * Sections with no dependencies come first.
 * C-1: Throws on circular dependencies.
 */
export function topologicalSort<T extends { id: string; dependsOn: string[] }>(sections: T[]): T[] {
  const visited = new Set<string>();
  const inProgress = new Set<string>();
  const result: T[] = [];
  const sectionMap = new Map(sections.map(s => [s.id, s]));

  function visit(section: T) {
    if (visited.has(section.id)) return;
    if (inProgress.has(section.id)) {
      throw new Error(`Circular dependency detected involving section "${section.id}"`);
    }
    inProgress.add(section.id);
    for (const depId of section.dependsOn ?? []) {
      const dep = sectionMap.get(depId);
      if (dep) visit(dep);
    }
    inProgress.delete(section.id);
    visited.add(section.id);
    result.push(section);
  }

  for (const section of sections) {
    visit(section);
  }

  return result;
}

/**
 * Replace {{section:domain:artifactType}} tokens in an execution prompt
 * with actual artifact IDs from completed sections.
 *
 * GAP-4: Returns both the enriched prompt and a list of unresolved tokens.
 * Callers should check unresolvedTokens and warn the user if non-empty.
 */
export function enrichExecutionPrompt(
  prompt: string,
  artifacts: Record<string, Record<string, string[]>>,
  options?: {
    domain?: PlanSection['domain'];
  },
): { enrichedPrompt: string; unresolvedTokens: string[] } {
  const unresolvedTokens: string[] = [];
  const runtimeTokenReplacements: Record<string, string> = {
    '{{section:auth:userId}}': '(auth context is resolved at runtime)',
    '{{section:auth:context}}': '(auth context is resolved at runtime)',
  };
  const visualArtifacts = artifacts['visual-editor'];
  const shouldSanitizeVisualArtifacts = options?.domain === 'interaction' || options?.domain === 'workflow';
  const resolvedVisualArtifacts = visualArtifacts
    ? (
        shouldSanitizeVisualArtifacts
          ? sanitizeVisualArtifactsForDownstreamPrompt({
              componentIds: visualArtifacts.componentIds,
              componentSummary: visualArtifacts.componentSummary,
            })
          : {
              componentIds: visualArtifacts.componentIds || [],
              componentSummary: visualArtifacts.componentSummary || [],
            }
      )
    : { componentIds: [], componentSummary: [] };
  let enrichedPrompt = prompt.replace(
    /\{\{section:([a-zA-Z0-9_-]+):([a-zA-Z0-9_]+)\}\}/g,
    (match, domain: string, artifactType: string) => {
      const runtimeReplacement = runtimeTokenReplacements[match];
      if (runtimeReplacement) {
        return runtimeReplacement;
      }

      const domainArtifacts = artifacts[domain];
      const resolvedVisualArtifact =
        artifactType === 'componentIds'
          ? resolvedVisualArtifacts.componentIds
          : artifactType === 'componentSummary'
            ? resolvedVisualArtifacts.componentSummary
            : undefined;
      const resolvedArtifact =
        domain === 'visual-editor'
          ? resolvedVisualArtifact
          : domainArtifacts?.[artifactType];
      if (!resolvedArtifact || resolvedArtifact.length === 0) {
        unresolvedTokens.push(match);
        return `[NOTE: ${domain} ${artifactType} not yet available — will be created by a prior phase]`;
      }

      // For large componentIds lists, use typed summary to reduce prompt bloat
      // and help the interaction phase identify buttons, inputs, charts, etc.
      const MAX_RAW_IDS = 12;
      if (
        artifactType === 'componentIds' &&
        resolvedArtifact.length >= MAX_RAW_IDS &&
        (domain === 'visual-editor' ? resolvedVisualArtifacts.componentSummary.length : domainArtifacts['componentSummary']?.length)
      ) {
        const INTERACTIVE_TYPES = new Set([
          'button', 'input', 'modal', 'chart', 'table', 'data-text',
          'gauge-chart', 'toggle-switch', 'form-container',
        ]);
        const summaryLines =
          domain === 'visual-editor'
            ? resolvedVisualArtifacts.componentSummary
            : domainArtifacts['componentSummary'];
        const relevant = summaryLines
          .filter((line: string) => {
            const m = line.match(/^([^:]+):/);
            return m && INTERACTIVE_TYPES.has(m[1].trim());
          });
        if (relevant.length > 0) {
          return relevant.join('; ');
        }
      }

      return resolvedArtifact.join(', ');
    },
  );

  // Auto-inject schema context into prompts when data artifacts are available
  // This ensures component/interaction phases have full schema details for data binding
  const dataArtifacts = artifacts['data'];
    if (dataArtifacts?.schemaContext?.length && !enrichedPrompt.includes('[DATA SCHEMAS')) {
      const schemaBlock = `\n\n[DATA SCHEMAS — created in prior phase, use these exact IDs]\n${dataArtifacts.schemaContext.join('\n')}`;
      enrichedPrompt += schemaBlock;
      if (dataArtifacts.groupIds?.length) {
        enrichedPrompt += `\nFor chart/table/PM read bindings: persist source identity at properties.dataBinding.source and field mappings at properties.dataBinding.fieldMapping.`;
        enrichedPrompt += `\nSchema read bindings require properties.dataBinding.source with kind="schema", dataOwnerOrganizationId, ownerType, ownerId, groupId, and schemaId from the chosen catalog source summary.`;
        enrichedPrompt += `\nDo not persist Phase 11B read bindings through legacy dataSource/queryDefinition organization fields, template-id fields, or legacy composite-query dataSource fields.`;
      }
    }

  // Auto-inject query template context so the DataComponentAgent can reference
  // pre-built templates as full DataViewRef identities instead of legacy template-id fields.
  if (dataArtifacts?.queryTemplateContext?.length && !enrichedPrompt.includes('[QUERY TEMPLATES')) {
    enrichedPrompt += `\n\n[QUERY TEMPLATES — pre-built in data phase, use full query-template view identity for matching KPI/chart bindings]\n${dataArtifacts.queryTemplateContext.join('\n')}`;
    enrichedPrompt += `\nWhen a template's measures match a component's needs, bind through properties.dataBinding.source with kind="query-template", viewId, ownerType, ownerId, and viewOrganizationId from the matching view summary, plus properties.dataBinding.fieldMapping for component fields.`;
    enrichedPrompt += `\nFor federated-view summaries, use properties.dataBinding.source with kind="federated-view", viewId, ownerType, ownerId, and viewOrganizationId. Ask for clarification if the full view identity is not available.`;
  }

  const shouldAddPhase11BBindingGuidance =
    options?.domain === 'data' ||
    Boolean(dataArtifacts?.schemaContext?.length) ||
    Boolean(dataArtifacts?.queryTemplateContext?.length);
  if (
    shouldAddPhase11BBindingGuidance &&
    !enrichedPrompt.includes('[BINDING RULES - Phase 11B')
  ) {
    enrichedPrompt += `

${formatPhase11BBindingInstructionsForPrompt()}`;
  }

  const shouldAddPhase11CWriteTargetGuidance =
    options?.domain === 'data' ||
    options?.domain === 'workflow' ||
    options?.domain === 'interaction' ||
    Boolean(dataArtifacts?.schemaContext?.length) ||
    Boolean(dataArtifacts?.queryTemplateContext?.length) ||
    Boolean(resolvedVisualArtifacts.componentSummary.length) ||
    Boolean(resolvedVisualArtifacts.componentIds.length);
  if (
    shouldAddPhase11CWriteTargetGuidance &&
    !enrichedPrompt.includes('[WRITE-TARGET RULES - Phase 11C')
  ) {
    enrichedPrompt += `

${formatPhase11CWriteTargetInstructionsForPrompt()}`;
  }

  // Auto-inject component map so interaction/data phases can resolve semantic
  // roles (e.g., "the submit button") to actual generated component IDs.
  // This is the primary defense against Phase 3 referencing wrong/generic IDs.
  const veArtifacts = artifacts['visual-editor'];
  if (resolvedVisualArtifacts.componentSummary.length && !enrichedPrompt.includes('[COMPONENT MAP')) {
    enrichedPrompt += `\n\n[COMPONENT MAP — actual component IDs from prior phase, grouped by type. Use these exact IDs when targeting components.]\n${resolvedVisualArtifacts.componentSummary.join('\n')}`;
  }

  // Auto-inject page context into interaction phases for navigate-page actions
  if (veArtifacts?.pageContext?.length && !enrichedPrompt.includes('[FORM PAGES')) {
    enrichedPrompt += `\n\n[FORM PAGES — use these pageIds for navigate-page interactions]\n${veArtifacts.pageContext.join('\n')}`;
  }

  // Clean up misleading [NOTE: ... not yet available] placeholders when the
  // corresponding context block has already been injected. The LLM sees "not yet
  // available" and hallucinates missing data, even though it's right below in the
  // auto-injected COMPONENT MAP / DATA SCHEMAS / QUERY TEMPLATES blocks.
  const hasComponentMap = enrichedPrompt.includes('[COMPONENT MAP');
  const hasDataSchemas = enrichedPrompt.includes('[DATA SCHEMAS');
  const hasQueryTemplates = enrichedPrompt.includes('[QUERY TEMPLATES');

  if (hasComponentMap || hasDataSchemas || hasQueryTemplates) {
    enrichedPrompt = enrichedPrompt.replace(
      /\[NOTE: ([a-zA-Z0-9_-]+) [a-zA-Z0-9_]+ not yet available — will be created by a prior phase\]/g,
      (_match, domain: string) => {
        if (domain === 'visual-editor' && hasComponentMap) {
          return '(see COMPONENT MAP below)';
        }
        if (domain === 'data' && (hasDataSchemas || hasQueryTemplates)) {
          return '(see DATA SCHEMAS and QUERY TEMPLATES below)';
        }
        return `(${domain} context is resolved at runtime)`;
      },
    );
    // Remove tokens from unresolvedTokens that were cleaned up — they're not truly
    // unresolved, just using non-standard artifact keys that the context blocks cover.
    const resolvedDomains = new Set<string>();
    if (hasComponentMap) resolvedDomains.add('visual-editor');
    if (hasDataSchemas || hasQueryTemplates) resolvedDomains.add('data');
    const trulyUnresolved = unresolvedTokens.filter((token) => {
      if (runtimeTokenReplacements[token]) return false;
      const m = token.match(/\{\{section:([^:}]+):/);
      return !m || !resolvedDomains.has(m[1]);
    });
    unresolvedTokens.length = 0;
    unresolvedTokens.push(...trulyUnresolved);
  }

  return { enrichedPrompt, unresolvedTokens };
}
